import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ArrowLeft, ArrowRight, Clock, Package } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useMyProfessional } from '@/hooks/useProfessionalsApi'
import { formatCurrency } from '@/lib/dashboard-utils'

type ProfessionalServicesStepProps = {
  onNext: () => void
  onBack: () => void
  onSkip: () => void
}

export function ProfessionalServicesStep({ onNext, onBack, onSkip }: ProfessionalServicesStepProps) {
  const { t } = useTranslation('onboarding')
  const myProfessional = useMyProfessional()

  const assignments = myProfessional.data?.professional?.assignments ?? []

  return (
    <div className="flex flex-col px-4 py-6 animate-in fade-in duration-500">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F08A24] to-[#f9a84c] mb-4">
          <Package weight="bold" className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-[#0B2A4A] dark:text-white mb-2">
          {t('professional.services.title')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {t('professional.services.subtitle')}
        </p>
      </div>

      <div className="max-w-lg mx-auto w-full">
        {myProfessional.isLoading ? (
          <div className="h-32 w-full animate-pulse rounded-lg bg-muted" />
        ) : assignments.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="font-medium text-gray-700 dark:text-gray-200 mb-1">
                {t('professional.services.emptyTitle')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('professional.services.emptyDescription')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {assignments.map((assignment) => (
              <Card key={assignment.id}>
                <CardContent className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 dark:text-gray-100 truncate">
                      {assignment.packageTitle}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                      {formatCurrency(assignment.priceCents, assignment.currency)}
                      {assignment.durationMinutes ? (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {t('professional.services.duration', { minutes: assignment.durationMinutes })}
                        </span>
                      ) : null}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-10 max-w-lg mx-auto w-full">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
        >
          <ArrowLeft weight="bold" className="mr-2 w-4 h-4" />
          {t('back')}
        </Button>

        <div className="flex items-center gap-3">
          {assignments.length === 0 && (
            <Button
              onClick={onSkip}
              variant="ghost"
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500"
            >
              {t('skipStep')}
            </Button>
          )}
          <Button onClick={onNext} className="bg-[#F08A24] hover:bg-[#e07a14] text-white">
            {t('continue')}
            <ArrowRight weight="bold" className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
